import { useEffect, useRef } from "react";
import { useLocalStorage } from "usehooks-ts";

import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";

const SHOW_POST_EDITOR_KEY = "showPostEditor";

const isActiveElementEditable = () => {
  const element = document.activeElement;
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    element?.attributes.getNamedItem("contenteditable")?.value === "true"
  );
};

export const usePostEditor = () => {
  const [showEditor, setShowEditor] = useLocalStorage(
    SHOW_POST_EDITOR_KEY,
    true,
  );
  const { toast, dismiss } = useToast();
  const currentToastId = useRef<string>(null!);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isActiveElementEditable()) return;

      if (
        event.key.toLowerCase() === "p" &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey &&
        !event.metaKey
      ) {
        setShowEditor((prev) => {
          if (!prev) {
            const { id } = toast({
              variant: "default",
              title: "Post Editor Enabled",
              description: (
                <div className="mt-2">
                  Press the{" "}
                  <Badge variant="outline" className="mx-2 inline-flex">
                    P
                  </Badge>{" "}
                  key to toggle it on/off.
                </div>
              ),
              duration: 3000,
            });
            currentToastId.current = id;
          } else {
            dismiss(currentToastId.current);
          }

          return !prev;
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [setShowEditor, toast, dismiss]);

  return {
    showEditor,
    setShowEditor,
  };
};
